// dependencies
import { z } from "zod";
import { useSelector, useDispatch } from "react-redux";
import React, { useEffect, useRef, useState } from "react";
import { getMatchedRouteKeyValue, replace } from "yarrr";
import CreatableSelect from "react-select/creatable";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

// components
import Grid from "../../../../Components/Grid";
import Table from "../../../../Components/TableLite";
import Button from "../../../../Components/Button";
import Spacer from "../../../../Components/Spacer";
import Text from "../../../../Components/Inputs/Text";
import Radio from "../../../../Components/Inputs/Radio";
import Textarea from "../../../../Components/Inputs/Textarea";
import Multiselect from "../../../../Components/Inputs/Multiselect";
import useForm from "../../../../Hooks/useForm";
import request from "../../../../Api/request";
import { useFeatureFlag } from "../../../../Api/hooks";
import { getCurrentEnvironment } from "../../../../Selectors/app";
import { useEnvironment } from "../../../../Hooks";

// css
import css from "./BulkUpdate.styl";

const jsonPlaceholder = (slug, type) => {
	const json = {
		name: "Company",
		max_users: {
			$gt: 0,
		},
		active_users: {
			$gt: 0,
		},
		"authentication_methods.google.state": "disabled",
		default_timezone: "America/New_York",
		"engagement.frequency": {
			$gt: 0,
		},
	};

	if (type === "boolean") {
		json[`feature_flag.${slug}`] = true;
	}

	if (type === "string") {
		json[`feature_flag.${slug}`] = "";
	}

	if (type === "number") {
		json[`feature_flag.${slug}`] = {
			$gt: 0,
		};
	}

	return JSON.stringify(json, null, 4);
};

// Helper function to parse input values (comma or line break separated)
const parseInputValues = (input) => {
	if (!input || typeof input !== "string") return [];

	// Split by commas or line breaks, then clean up each value
	return input
		.split(/[,\n\r]+/)
		.map((value) => value.trim())
		.filter((value) => value.length > 0);
};

const BulkUpdateFeatureFlag = () => {
	const dispatch = useDispatch();
	const isInitialStateSet = useRef(false);
	const [companies, setCompanies] = useState([]);
	const [flagValue, setFlagValue] = useState("");
	const [json, setJson] = useState("");
	const [queryMode, setQueryMode] = useState("manual"); // "manual" or "field"
	const [selectedField, setSelectedField] = useState("salesforceID");
	const [fieldValuesInput, setFieldValuesInput] = useState(""); // contains the values to search for when using field-based search mode
	const environment = useEnvironment();
	const slug = useSelector((state) => getMatchedRouteKeyValue(state, "slug", null));

	const featureFlagQuery = useFeatureFlag(environment, slug);

	if (featureFlagQuery.isLoading) {
		return <div>Loading...</div>;
	}

	if (!isInitialStateSet.current) {
		setFlagValue(featureFlagQuery.data.featureFlag.initialValue.toString());
		setJson(jsonPlaceholder(slug, featureFlagQuery.data.featureFlag.type));
		isInitialStateSet.current = true;
	}

	const columns = [
		{
			id: "_id",
			label: "ID",
			width: "1fr",
			getter: (row) => row._id,
		},
		{
			id: "name",
			label: "Name",
			width: "1fr",
			getter: (row) => row.name,
		},
		{
			id: "active_users",
			label: "Active Users",
			width: "1fr",
			getter: (row) => row.active_users,
		},
	];

	const fieldOptions = [{ id: "salesforceID", label: "Salesforce ID" }];

	const handleQueryCompanies = async () => {
		if (queryMode === "manual") {
			try {
				const response = await request({
					url: `/${environment}/direct-query/company`,
					method: "POST",
					data: {
						match: JSON.parse(json),
					},
				});
				setCompanies(response.data.response);
				if (response.data.response.length === 0) {
					dispatch({
						type: "[100] create info toast",
						message: "No companies found matching the query",
						actionMessage: "Dismiss",
					});
				} else {
					dispatch({
						type: "[100] create info toast",
						message: `Found ${response.data.response.length} companies`,
						actionMessage: "Dismiss",
					});
				}
			} catch (error) {
				console.error("Query error:", error);
				dispatch({
					type: "[100] create error toast",
					message: "Error executing query. Please check your JSON syntax.",
					actionMessage: "Dismiss",
				});
			}
		} else {
			// Field-based query
			const parsedValues = parseInputValues(fieldValuesInput);

			if (parsedValues.length === 0) {
				dispatch({
					type: "[100] create error toast",
					message: "Please enter values to search for",
					actionMessage: "Dismiss",
				});
				return;
			}

			try {
				// Use the field-query endpoint for all field-based searches
				const response = await request({
					url: `/${environment}/field-query/company`,
					method: "POST",
					data: {
						field: selectedField,
						values: parsedValues,
					},
				});

				setCompanies(response.data.response);

				// Show detailed feedback based on the response
				if (response.data.message) {
					dispatch({
						type: "[100] create info toast",
						message: response.data.message,
						actionMessage: "Dismiss",
					});
				}
			} catch (error) {
				let errorMessage = "Error executing field query";

				// Try to extract more specific error message
				if (error.response?.data?.errors) {
					errorMessage = error.response.data.errors.join(", ");
				} else if (error.message) {
					errorMessage = error.message;
				}

				dispatch({ type: "[100] create error toast", message: errorMessage, actionMessage: "Dismiss" });
			}
		}
	};

	const handleBulkUpdate = async () => {
		const response = await request({
			url: `/${environment}/feature-flags/bulk-update/${slug}`,
			method: "PATCH",
			data: {
				comment: `Bulk update feature flag ${slug} to ${flagValue} for ${companies.length} companies matching ${
					queryMode === "manual" ? json : `${selectedField}: ${fieldValuesInput}`
				}`,
				companyIds: companies.map((company) => company._id),
				value: flagValue,
			},
		});

		dispatch({ type: "[100] create info toast", message: "Updated Flag Values", actionMessage: "Dismiss" });
		featureFlagQuery.refetch();
	};

	return (
		<div>
			<div className={css.content}>
				<div className={css.section}>
					<div className={css.label} style={{ color: "#888", fontWeight: 600, fontSize: "13px" }}>
						Feature Flag
					</div>
					<Spacer size={1} />
					<h1>{featureFlagQuery.data.featureFlag.name}</h1>

					<Spacer size={3} />

					<div className={css.sectionInformation}>
						<div className={css.title}>Feature Flag Bulk Update</div>
						<Grid columns={["600px", "1fr", "120px"]}>
							<div>
								<div className={css.description}>
									You can use this page to bulk update feature flags. Choose between manual query
									writing or field-specific searching for easier targeting of companies.
								</div>
							</div>

							<div />

							<div />
						</Grid>
					</div>

					<Spacer size={4} />

					<div className={css.sectionContent}>
						<div className={css.label}>New Feature Flag Value</div>
						{featureFlagQuery.data.featureFlag.type === "boolean" ? (
							<Radio
								options={[
									{ id: "true", label: "True" },
									{ id: "false", label: "False" },
								]}
								value={flagValue}
								onChange={(val) => {
									setFlagValue(val);
								}}
							/>
						) : null}

						{featureFlagQuery.data.featureFlag.type === "string" ? (
							<CreatableSelect
								isClearable={true}
								isSearchable={true}
								onChange={(update, action) => {
									if (action.action === "create-option") {
										setFlagValue(update.value);
									} else if (action.action === "select-option") {
										setFlagValue(update.id);
									} else if (action.action === "clear") {
										setFlagValue("");
									}
								}}
								defaultValue={{ value: flagValue, label: flagValue }}
								options={featureFlagQuery.data.featureFlag.options.elements.map((element) => ({
									id: element,
									label: element,
								}))}
							/>
						) : null}

						{featureFlagQuery.data.featureFlag.type === "number" ? (
							<Text
								value={flagValue}
								onChange={(val) => {
									setFlagValue(val);
								}}
							/>
						) : null}

						{featureFlagQuery.data.featureFlag.type === "json" ? (
							<Textarea
								value={flagValue}
								onChange={(val) => {
									setFlagValue(val);
								}}
							/>
						) : null}
					</div>

					<Spacer size={4} />

					<div className={css.sectionContent}>
						<div className={css.label}>Query Method</div>
						<Radio
							options={[
								{ id: "manual", label: "Manual Query (JSON)" },
								{ id: "field", label: "Field-based Search" },
							]}
							value={queryMode}
							onChange={(val) => {
								setQueryMode(val);
								setCompanies([]); // Clear companies when switching modes
							}}
						/>
					</div>

					<Spacer size={4} />

					{queryMode === "manual" ? (
						<div className={css.sectionContent}>
							<div className={css.label}>Companies To Update (Manual Query)</div>
							<Grid columns={["1fr", "100px"]}>
								<div>
									<Textarea
										value={json}
										initialHeight={360}
										onChange={(val) => {
											setJson(val);
										}}
									/>
								</div>
								<div>
									<Button color="black" onClick={handleQueryCompanies}>
										Query
									</Button>
								</div>
							</Grid>
						</div>
					) : (
						<div className={css.sectionContent}>
							<div className={css.label}>Field-based Search</div>
							<Spacer size={2} />

							<div className={css.label} style={{ fontSize: "12px", color: "#666" }}>
								Search Field
							</div>
							<Multiselect
								options={fieldOptions}
								value={selectedField}
								onChange={(val) => {
									setSelectedField(val);
									setCompanies([]); // Clear companies when switching fields
								}}
							/>

							<Spacer size={3} />

							<div className={css.label} style={{ fontSize: "12px", color: "#666" }}>
								Values (comma or line break separated)
							</div>
							<Grid columns={["1fr", "100px"]}>
								<div>
									<Textarea
										value={fieldValuesInput}
										initialHeight={200}
										placeholder={
											selectedField === "salesforceID"
												? "Enter Salesforce IDs separated by commas or line breaks:\n0031U00000abc123\n0031U00000def456\n0031U00000ghi789"
												: selectedField === "name"
													? "Enter company names separated by commas or line breaks:\nAcme Corp\nExample Inc\nTest Company"
													: "Enter company IDs separated by commas or line breaks:\n507f1f77bcf86cd799439011\n507f1f77bcf86cd799439012"
										}
										onChange={(val) => {
											setFieldValuesInput(val);
										}}
									/>
								</div>
								<div>
									<Button color="black" onClick={handleQueryCompanies}>
										Query
									</Button>
								</div>
							</Grid>

							{selectedField === "salesforceID" && (
								<>
									<Spacer size={2} />
									<div style={{ fontSize: "12px", color: "#888", fontStyle: "italic" }}>
										Note: Salesforce IDs are stored in the Logan database and will be
										cross-referenced with company data automatically.
									</div>
								</>
							)}
						</div>
					)}

					{companies.length > 0 ? (
						<>
							<Spacer size={4} />

							<div className={css.highlightSection}>
								<Grid gap={24} columns={["1fr", "260px"]}>
									<div className={css.label}>
										Update <span className={css.highlight}>{companies.length}</span> Companies?
									</div>
									<Button onClick={handleBulkUpdate}>Yes, Update {companies.length} Companies</Button>
								</Grid>
							</div>
						</>
					) : null}

					{companies.length > 0 ? (
						<>
							<Spacer size={4} />

							<div className={css.sectionContent}>
								<div className={css.label}>Selected Companies</div>
								<Table rows={companies} columns={columns} />
							</div>
						</>
					) : null}
				</div>
			</div>
		</div>
	);
};

export default BulkUpdateFeatureFlag;
